/**
 * useTone Hook - Manages Tone.js lifecycle and operations
 * Provides React integration for ToneService
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as Tone from 'tone';
import { toneService } from '../services/tone/ToneService';
import type { InstrumentType, ADSRSettings } from '../types/composition';
import type { InstrumentParameters } from '../types/instruments';

/**
 * Step change callback type
 */
export type StepChangeCallback = (step: number) => void;

/**
 * Return type for useTone hook
 */
export interface UseToneReturn {
  /** Whether Tone.js is initialized and ready */
  isReady: boolean;
  /** Start playback */
  play: () => Promise<void>;
  /** Pause playback */
  pause: () => void;
  /** Stop playback */
  stop: () => void;
  /** Set BPM */
  setBpm: (bpm: number) => void;
  /** Set loop mode */
  setLoop: (enabled: boolean) => void;
  /** Set volume for a specific row */
  setVolume: (row: number, volume: number) => void;
  /** Mute/unmute a specific row */
  setMute: (row: number, muted: boolean) => void;
  /** Update parameters for a specific row */
  updateVoiceParameters: (row: number, parameters: InstrumentParameters) => void;
  /** Export audio as WAV blob */
  exportAudio: () => Promise<Blob>;
  /** Get current transport state */
  getState: () => 'started' | 'paused' | 'stopped';
}

/**
 * Props for useTone hook
 */
export interface UseToneProps {
  /** Array of instrument types */
  instrumentTypes: InstrumentType[];
  /** Grid state (active cells) */
  gridState: boolean[][];
  /** Note state (pitches) */
  noteState: (string | null)[][];
  /** Number of steps */
  steps: number;
  /** Beats per minute */
  bpm: number;
  /** Optional ADSR settings per instrument (legacy support) */
  adsrSettings?: ADSRSettings[];
  /** Optional instrument parameters per instrument */
  instrumentParameters?: (InstrumentParameters | undefined)[];
  /** Optional array of flags indicating whether to apply parameters per instrument */
  parametersEnabled?: boolean[];
  /** Callback when step changes during playback */
  onStepChange?: StepChangeCallback;
}

/**
 * Custom hook for managing Tone.js integration
 * 
 * @param props - Hook configuration
 * @returns Tone.js control methods and state
 */
export function useTone({
  instrumentTypes,
  gridState,
  noteState,
  steps,
  bpm,
  adsrSettings,
  instrumentParameters,
  parametersEnabled,
  onStepChange,
}: UseToneProps): UseToneReturn {
  const [isReady, setIsReady] = useState(false);
  const isInitializedRef = useRef(false);

  /**
   * Initialize Tone.js on mount
   */
  useEffect(() => {
    // Don't auto-initialize - wait for user interaction
    // This is handled in the play() method
    
    return () => {
      // Clean up on unmount
      toneService.dispose();
      isInitializedRef.current = false;
      setIsReady(false);
    };
  }, []);

  /**
   * Track previous state to detect when voices/sequencer need updates
   */
  const prevInstrumentsRef = useRef<string>('');
  const prevParametersEnabledRef = useRef<string>('');
  const prevParametersRef = useRef<string>('');
  
  /**
   * Recreate voices only when instrument types or parametersEnabled changes
   * IMPORTANT: Stop playback first to prevent timing issues
   */
  useEffect(() => {
    if (isReady && instrumentTypes.length > 0) {
      // Serialize current state for comparison
      const currentInstruments = JSON.stringify(instrumentTypes);
      const currentParametersEnabled = JSON.stringify(parametersEnabled);
      
      // Check if we need to recreate voices
      const needsVoiceRecreation = 
        currentInstruments !== prevInstrumentsRef.current ||
        currentParametersEnabled !== prevParametersEnabledRef.current;
      
      if (needsVoiceRecreation) {
        // Get current transport state
        const wasPlaying = toneService.getState() === 'started';
        
        // Stop playback to prevent timing issues during voice recreation
        if (wasPlaying) {
          toneService.stop();
        }
        
        // Recreate voices with new instruments and parameters
        toneService.createVoices(instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled);
        
        // Reinitialize sequencer with new voices
        toneService.initSequencer(
          gridState,
          noteState,
          steps,
          bpm,
          onStepChange
        );
        
        // Resume playback if it was playing
        if (wasPlaying) {
          // Use setTimeout to ensure voice creation is complete
          setTimeout(() => {
            toneService.play().catch(err => {
              console.error('Failed to resume playback after voice recreation:', err);
            });
          }, 50);
        }
        
        // Update refs
        prevInstrumentsRef.current = currentInstruments;
        prevParametersEnabledRef.current = currentParametersEnabled;
      }
    }
  }, [instrumentTypes, parametersEnabled, isReady, gridState, noteState, steps, bpm, onStepChange, adsrSettings, instrumentParameters]);
  
  /**
   * Update voice parameters in real-time when only parameters change
   * This avoids recreating voices and maintains smooth playback
   */
  useEffect(() => {
    if (isReady && instrumentParameters && instrumentParameters.length > 0) {
      const currentParameters = JSON.stringify(instrumentParameters);
      
      // Check if only parameters changed (not instruments or enabled flags)
      if (currentParameters !== prevParametersRef.current) {
        // Update parameters for each voice without recreating
        instrumentParameters.forEach((params, row) => {
          if (params && parametersEnabled?.[row]) {
            toneService.updateVoiceParameters(row, params);
          }
        });
        
        prevParametersRef.current = currentParameters;
      }
    }
  }, [instrumentParameters, parametersEnabled, isReady]);
  
  /**
   * Update sequencer when grid/notes change, but only if transport is stopped
   * This prevents interrupting playback
   */
  useEffect(() => {
    if (isReady && instrumentTypes.length > 0) {
      const transportState = toneService.getState();
      
      // Only update sequencer if not playing
      if (transportState === 'stopped') {
        toneService.initSequencer(
          gridState,
          noteState,
          steps,
          bpm,
          onStepChange
        );
      }
    }
  }, [gridState, noteState, steps, bpm, isReady, onStepChange]);

  /**
   * Play audio
   */
  const play = useCallback(async () => {
    try {
      // Initialize on first play
      if (!isReady) {
        await toneService.initialize();
        toneService.createVoices(instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled);
        toneService.initSequencer(
          gridState,
          noteState,
          steps,
          bpm,
          onStepChange
        );
        setIsReady(true);
        isInitializedRef.current = true;
      }
      
      await toneService.play();
    } catch (error) {
      console.error('Failed to start playback', error);
      throw error;
    }
  }, [isReady, instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled, gridState, noteState, steps, bpm, onStepChange]);

  /**
   * Pause audio
   */
  const pause = useCallback(() => {
    toneService.pause();
  }, []);

  /**
   * Stop audio
   */
  const stop = useCallback(() => {
    toneService.stop();
  }, []);

  /**
   * Set BPM
   */
  const setBpm = useCallback((newBpm: number) => {
    toneService.setBpm(newBpm);
  }, []);

  /**
   * Set loop mode
   */
  const setLoop = useCallback((enabled: boolean) => {
    toneService.setLoop(enabled);
  }, []);

  /**
   * Set volume for a row
   */
  const setVolume = useCallback((row: number, volume: number) => {
    toneService.setVolume(row, volume);
  }, []);

  /**
   * Mute/unmute a row
   */
  const setMute = useCallback((row: number, muted: boolean) => {
    toneService.setMute(row, muted);
  }, []);

  /**
   * Update voice parameters for a row
   */
  const updateVoiceParameters = useCallback((row: number, parameters: InstrumentParameters) => {
    toneService.updateVoiceParameters(row, parameters);
  }, []);

  /**
   * Export audio as WAV
   * Records exactly one loop of the composition
   */
  const exportAudio = useCallback(async (): Promise<Blob> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Ensure Tone.js is initialized
        if (!isInitializedRef.current) {
          await toneService.play();
          toneService.stop();
          isInitializedRef.current = true;
          setIsReady(true);
        }

        // Reset transport to beginning
        Tone.getTransport().cancel();
        Tone.getTransport().position = 0;
        Tone.getTransport().bpm.value = bpm;

        // Rebuild voices and sequencer for clean recording
        toneService.createVoices(instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled);
        toneService.initSequencer(
          gridState,
          noteState,
          steps,
          bpm,
          undefined // No step callback during export
        );

        // Create recorder and connect to destination
        const recorder = new Tone.Recorder();
        Tone.getDestination().connect(recorder);

        // Start recording and playback
        await recorder.start();
        Tone.getTransport().start();

        // Calculate exact loop end time
        // Each step is a 16th note (1/4 of a beat)
        const secPerBeat = 60 / bpm;
        const stepDuration = secPerBeat / 4;
        const loopEndTime = steps * stepDuration;

        // Schedule stop at exact loop end
        Tone.getTransport().scheduleOnce(async (time) => {
          // Stop transport at exact time
          Tone.getTransport().stop(time);

          try {
            // Stop recorder and get WAV blob
            const wavBlob = await recorder.stop();

            // Disconnect recorder
            Tone.getDestination().disconnect(recorder);
            recorder.dispose();

            // Reinitialize sequencer for normal playback
            toneService.initSequencer(
              gridState,
              noteState,
              steps,
              bpm,
              onStepChange
            );

            resolve(wavBlob);
          } catch (err) {
            reject(err);
          }
        }, loopEndTime);

      } catch (error) {
        console.error('[useTone] Failed to export audio', error);
        reject(error);
      }
    });
  }, [steps, bpm, instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled, gridState, noteState, onStepChange]);

  /**
   * Get current transport state
   */
  const getState = useCallback(() => {
    return toneService.getState();
  }, []);

  return {
    isReady,
    play,
    pause,
    stop,
    setBpm,
    setLoop,
    setVolume,
    setMute,
    updateVoiceParameters,
    exportAudio,
    getState,
  };
}
